import styled from "styled-components";
import ReactPlayer from "react-player";
import { mobile } from "../responsive";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  padding: 80px 40px;
  min-height: 100vh;

  ${mobile({
    padding: "60px 20px",
  })}
`;

const Title = styled.h1`
  font-family: Jost-Bold;
  font-size: 48px;
  color: #ffffff;
  text-align: center;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  ${mobile({
    fontSize: "32px",
    marginBottom: "12px",
  })}
`;

const Subtitle = styled.p`
  font-family: Jost-Light;
  font-size: 18px;
  color: #a0a0a0;
  text-align: center;
  margin-bottom: 60px;
  max-width: 600px;

  ${mobile({
    fontSize: "16px",
    marginBottom: "40px",
  })}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  z-index: 10;
  margin-bottom: 50px;
  max-width: 1200px;

  ${mobile({
    gap: "8px",
    width: "100%",
  })}
`;

const Button = styled.button`
  background: ${(props) => 
    props.active 
      ? "linear-gradient(135deg, #FFD700 0%, #FFC700 100%)" 
      : "rgba(255, 255, 255, 0.05)"
  };
  color: ${(props) => (props.active ? "#000" : "#fff")};
  border: 1px solid ${(props) => (props.active ? "transparent" : "rgba(255, 255, 255, 0.1)")};
  padding: 14px 24px;
  cursor: pointer;
  font-size: 15px;
  border-radius: 12px;
  transition: all 0.3s ease;
  font-family: Jost-Medium;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  box-shadow: ${(props) => 
    props.active 
      ? "0 8px 24px rgba(255, 215, 0, 0.3)" 
      : "0 4px 12px rgba(0, 0, 0, 0.2)"
  };

  &:hover {
    background: ${(props) => 
      props.active 
        ? "linear-gradient(135deg, #FFD700 0%, #FFC700 100%)" 
        : "rgba(255, 255, 255, 0.1)"
    };
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(255, 215, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  ${mobile({
    fontSize: "13px",
    padding: "10px 16px",
    borderRadius: "10px",
  })}
`;

const VideoWrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
  background: #000;

  ${mobile({
    borderRadius: "16px",
    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.5)",
  })}
`;

const VideoContainer = styled.div`
  width: 100%;
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  
  .react-player {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const Video = () => {
  const intl = useIntl()
  const [videoUrl, setVideoUrl] = useState(
  intl.locale === "es" ? "https://youtu.be/lZIGA_hYRMQ" : "https://youtu.be/VLBcKdsY5Wk"
  );

  // Array of video data
  const videos = [
    {
      label: <FormattedMessage id="demo_title_1" defaultMessage="" />,
      chineseUrl: "https://youtu.be/lZIGA_hYRMQ",
      url: "https://youtu.be/VLBcKdsY5Wk",
    },
    {
      label: <FormattedMessage id="demo_title_2" defaultMessage="" />,
      chineseUrl: "https://youtu.be/7wGItg2vRWk",
      url: "https://youtu.be/z0uIRXHjJlE",
    },
    {
      label: <FormattedMessage id="demo_title_3" defaultMessage="" />,
      url: "https://youtu.be/wxnISitVyKQ",
      chineseUrl: "https://youtu.be/RX9Baz2dD2k",
    },
    {
      label: <FormattedMessage id="demo_title_4" defaultMessage="" />,
      url: "https://youtu.be/_vaEpNbPzbg",
      chineseUrl: "https://youtu.be/AsxdhK4Z7Ww",
    },
    {
      label: <FormattedMessage id="demo_title_5" defaultMessage="" />,
      url: "https://youtu.be/GS_oDwibyZA",
      chineseUrl: "https://youtu.be/1vuFmT0-uHI",
    },
    {
      label: <FormattedMessage id="demo_title_6" defaultMessage="" />,
      url: "https://youtu.be/OQsMxuBl9IY",
      chineseUrl: "https://youtu.be/Aor_8WQMuIY",
    },
    {
      label: <FormattedMessage id="demo_title_7" defaultMessage="" />,
      url: "https://youtu.be/jKDrW7KuOhM",
      chineseUrl: "https://youtu.be/nYmndJUzLXQ",
    },
  ];

  return (
    <Container>
      <Title>
        <FormattedMessage id="demo_main_title" defaultMessage="Product Demonstrations" />
      </Title>
      <Subtitle>
        <FormattedMessage id="demo_subtitle" defaultMessage="Explore our product features through high-quality video demonstrations" />
      </Subtitle>

      {/* Buttons Section */}
      <ButtonContainer>
        {videos.map((video) => (
          <Button
            key={video.url}
            active={videoUrl === (intl.locale === "es" ? video.chineseUrl : video.url)}
            onClick={() => setVideoUrl(intl.locale === "es" ? video.chineseUrl : video.url)}
          >
            {video.label}
          </Button>
        ))}
      </ButtonContainer>

      {/* Video Section */}
      <VideoWrapper>
        <VideoContainer>
          <ReactPlayer
            url={videoUrl}
            height="100%"
            width="100%"
            controls={false}
            playing={true}
            muted={false}
            loop={true}
            className="react-player"
            config={{
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  showinfo: 0,
                  iv_load_policy: 3,
                  disablekb: 1,
                  fs: 0,
                  color: 'white',
                  autohide: 1,
                }
              }
            }}
          />
        </VideoContainer>
      </VideoWrapper>
    </Container>
  );
};

export default Video;
